import { Instagram } from "lucide-react";
import { Button, EmptyState, Icon, compactNumber } from "@cntrl/ui";
import { useBoostRecs, type BoostRec } from "../../app/hooks/useMarketing.js";
import styles from "./RecommendedAds.module.scss";

/**
 * "Recommended for an ad" — the band's own Instagram posts, scored server-side by reach × engagement ×
 * velocity, with a one-tap route into the Ad Creator.
 *
 * **Three states, not two.** Scoring in flight ⇒ SKELETONS; nothing scored ⇒ one of two DIFFERENT empties
 * (unconnected has something to do, connected-with-nothing does not). Legacy shipped the loading state as the
 * empty card and its own spec records what that cost: "the 'no recommendations' card read as broken".
 */
export function RecommendedAds({
  onBoost,
  onConnect,
}: {
  onBoost: (rec: BoostRec) => void;
  /** The Settings escape, or null when it cannot be offered. The parent owns that decision because it is the
   *  same one for every door on this page: under the ROSTER lens an artist-shell route leaves the shell and
   *  lands an org seat on "No band selected" — legacy's own `roster-no-artist-view` rule (2026-07-06). */
  onConnect: (() => void) | null;
}) {
  const q = useBoostRecs();
  const recs = q.data?.recs ?? [];

  return (
    <section className={styles.section} aria-labelledby="recs-heading">
      <header className={styles.head}>
        <h2 id="recs-heading" className={styles.title}>
          Ad Recommendations
        </h2>
      </header>

      {q.isLoading ? (
        <ul className={styles.grid} aria-busy="true" aria-label="Scoring your posts">
          {[0, 1, 2, 3].map((i) => (
            <li key={i} className={`${styles.card} ${styles.skeleton}`} aria-hidden="true">
              <div className={styles.thumb} />
              <div className={styles.body}>
                <span className={styles.skelLine} />
                <span className={`${styles.skelLine} ${styles.skelLineSm}`} />
                <span className={styles.skelBtn} />
              </div>
            </li>
          ))}
        </ul>
      ) : recs.length > 0 ? (
        <ul className={styles.grid}>
          {recs.map((rec) => (
            <RecCard key={rec.id} rec={rec} onBoost={onBoost} />
          ))}
        </ul>
      ) : q.data?.connected ? (
        <EmptyState
          title="Nothing scored yet"
          description="We score your Instagram posts and surface the best ones to boost. Post something and check back."
        />
      ) : (
        <EmptyState
          title="Connect your socials to get recommendations"
          description="Once Instagram is connected we score your recent posts and surface the ones worth putting paid spend behind."
          action={
            onConnect ? (
              <Button variant="ghost" onClick={onConnect}>
                Open Settings → Connections
              </Button>
            ) : undefined
          }
        />
      )}
    </section>
  );
}

function RecCard({ rec, onBoost }: { rec: BoostRec; onBoost: (rec: BoostRec) => void }) {
  const caption = (rec.caption ?? "").trim() || "Instagram post";
  return (
    <li className={styles.card}>
      <div className={styles.thumb}>
        {rec.thumbnail ? <img className={styles.thumbImg} src={rec.thumbnail} alt="" loading="lazy" /> : null}
        <span className={styles.platformMark} aria-hidden="true">
          <Icon icon={Instagram} size={16} />
        </span>
      </div>
      <div className={styles.body}>
        {/* The caption LABELS the post; the metric is why it is on the list. That is why the small muted line
            comes first and the figure below it carries the display face — inverting them makes the card read
            as a post feed rather than as a shortlist. */}
        <p className={styles.caption} title={caption}>
          {caption}
        </p>
        <p className={styles.metric}>
          {compactNumber(rec.reach ?? 0)} reach · {compactNumber(rec.engagement ?? 0)} eng
        </p>
        <Button variant="accent" size="sm" onClick={() => onBoost(rec)}>
          Boost this
        </Button>
      </div>
    </li>
  );
}
